/**
 * Ход разбора одного входящего: `GET /api/inbox/:id` раз в 2 секунды (ТЗ §7).
 * Опрос ведёт `useInboxItem` — он же его выключает на терминальном статусе.
 *
 * ИИ-разбор обращения (классификация, подбор норм, финальный разбор) идёт обычно 5–10 минут,
 * длинное обращение — до 15, поэтому показываем честные стадии и счётчик времени, а не
 * безымянный индикатор: пользователь должен понимать, что такое ожидание — норма, а не зависание.
 *
 * `failed` — текст ошибки и «Повторить»; `needs_confirm` — экран подтверждения
 * сегментов; `done` — переход в заведённое дело.
 */

import { useEffect, useRef, useState } from 'react';
import { useConfirmSegments, useInboxItem, useRetryInbox } from '@/api/hooks';
import { errorMessage, FormError, LoadingState } from '@/components/ErrorState';
import { useOpenCase } from '@/components/Modal';
import { caseWord } from '@/lib/format';
import { INBOX_STATUS_LABELS } from '@/lib/stages';
import SegmentsConfirm from './SegmentsConfirm';
import { inboxStatusChipClass } from './InboxTable';

/** Галочка чек-бокса из макета: svg всегда в DOM, видимость переключает класс `.on`. */
function CheckMark() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="#fff"
            strokeWidth="3.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function elapsedText(seconds: number): string {
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    return `${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

export interface IngestProgressProps {
    inboxId: string;
    /** Уводить в дело, когда заведено ровно одно. На экране «Входящие» выключено. */
    autoOpenCase?: boolean;
    /**
     * Чем открывать дело. По умолчанию — карточка поверх текущего экрана.
     * Когда прогресс сам живёт в модалке, родитель сначала закрывает её,
     * иначе два окна встанут друг на друга.
     */
    onOpenCase?: (caseId: string) => void;
    onClose?: () => void;
}

export default function IngestProgress({
    inboxId,
    autoOpenCase = false,
    onOpenCase,
    onClose,
}: IngestProgressProps) {
    const openCaseOverScreen = useOpenCase();
    const openCase = onOpenCase ?? openCaseOverScreen;
    const query = useInboxItem(inboxId);
    const retry = useRetryInbox();
    const confirm = useConfirmSegments();

    const item = query.data;
    const status = item?.status;
    const pending = status === undefined || status === 'new' || status === 'processing';

    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        setElapsed(0);
        if (!pending) return undefined;
        const timer = window.setInterval(() => setElapsed((value) => value + 1), 1000);
        return () => window.clearInterval(timer);
    }, [inboxId, pending]);

    // Одно дело — открываем его сразу, как просит ТЗ §7. Несколько — показываем список.
    const openedRef = useRef(false);
    useEffect(() => {
        if (!autoOpenCase || openedRef.current) return;
        if (item && item.status === 'done' && item.cases.length === 1) {
            openedRef.current = true;
            openCase(item.cases[0].case_id);
        }
    }, [autoOpenCase, item, openCase]);

    const steps = [
        { label: 'Обращение принято и зарегистрировано во входящих', done: Boolean(item) },
        {
            label: 'Разбор вложения, обезличивание и классификация',
            done: status === 'done' || status === 'needs_confirm',
        },
        {
            label:
                status === 'needs_confirm'
                    ? 'Подтвердите, на сколько обращений делить текст'
                    : status === 'failed'
                      ? 'Дело не заведено — разбор прерван'
                      : 'Дело заведено и назначено исполнителю',
            done: status === 'done',
        },
    ];
    const doneCount = steps.filter((step) => step.done).length;

    return (
        <div className="stat-block">
            <div className="block-header">
                <span>⏳ Разбор обращения</span>
                {status ? (
                    <span className={inboxStatusChipClass(status)}>{INBOX_STATUS_LABELS[status]}</span>
                ) : null}
            </div>

            <div className="block-body">
                <FormError>{query.isError ? errorMessage(query.error) : null}</FormError>
                {query.isPending && !item ? <LoadingState variant="inline" label="Запрашиваем статус обработки…" /> : null}

                <div className="checklist-card">
                    <div className="checklist-head">
                        <div className="checklist-title">Что происходит с обращением</div>
                        <div className="checklist-progress">
                            {doneCount} из {steps.length}
                        </div>
                    </div>
                    <div className="checklist-bar">
                        <span style={{ width: `${Math.round((doneCount / steps.length) * 100)}%` }} />
                    </div>
                    {steps.map((step, position) => (
                        <div className={`check-item${step.done ? ' done' : ''}`} key={step.label}>
                            <span className={`check-box${step.done ? ' on' : ''}`} aria-hidden="true">
                                <CheckMark />
                            </span>
                            <span className="check-src">шаг {position + 1}</span>
                            <span className="check-text">{step.label}</span>
                        </div>
                    ))}
                </div>

                {pending ? (
                    <div className="draft-note">
                        Идёт разбор, прошло {elapsedText(elapsed)}. ИИ-разбор занимает обычно 5–10 минут,
                        длинное обращение — до 15. Страницу можно не держать открытой, статус виден во «Входящих».
                    </div>
                ) : null}

                {status === 'failed' ? (
                    <>
                        <FormError>
                            {item?.error || 'Разбор завершился ошибкой. Текст ошибки сервер не вернул.'}
                        </FormError>
                        <FormError>{retry.isError ? errorMessage(retry.error) : null}</FormError>
                        <div className="draft-bar">
                            <button
                                type="button"
                                className="act-btn primary"
                                disabled={retry.isPending}
                                onClick={() => retry.mutate(inboxId)}
                            >
                                {retry.isPending ? 'Отправляем…' : 'Повторить'}
                            </button>
                            {onClose ? (
                                <button type="button" className="act-btn" onClick={onClose}>
                                    Закрыть
                                </button>
                            ) : null}
                            <span className="draft-note">
                                Обращение осталось во входящих — повтор запускает разбор заново, ничего не теряя.
                            </span>
                        </div>
                    </>
                ) : null}

                {status === 'needs_confirm' && item ? (
                    <div className="case-section">
                        <SegmentsConfirm
                            segments={item.segments ?? []}
                            pending={confirm.isPending}
                            error={confirm.isError ? errorMessage(confirm.error) : null}
                            onConfirm={(groups) => confirm.mutate({ inboxId, groups })}
                            onCancel={onClose}
                        />
                    </div>
                ) : null}

                {status === 'done' && item ? (
                    <div className="case-section">
                        <h4>
                            Заведено {item.cases.length} {caseWord(item.cases.length)}
                        </h4>
                        {item.cases.length === 0 ? (
                            <div className="detail-empty">
                                Разбор завершён, но дела не созданы. Проверьте входящее — возможно, обращение
                                распознано как повторное.
                            </div>
                        ) : null}
                        <div className="draft-bar">
                            {item.cases.map((ref) => (
                                <button
                                    key={ref.case_id}
                                    type="button"
                                    className="act-btn primary"
                                    onClick={() => openCase(ref.case_id)}
                                >
                                    Открыть {ref.number}
                                </button>
                            ))}
                            {onClose ? (
                                <button type="button" className="act-btn" onClick={onClose}>
                                    Закрыть
                                </button>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
